import { FC, JSX } from "react";
import { FormikErrors, FormikProps, FormikTouched } from "formik";
import { useLingui } from "@lingui/react";
import { t } from "@lingui/macro";
import { I18n } from "@lingui/core";
import { StoreKey } from "src/lib/Intl";
import { storeKey } from "src/config/app-config";
import { Profile } from "src/molecules/ProfileForm/ProfileForm";
import {
  countryOptions,
  checkoutCOMCountryOptions,
  usStateOptions,
} from "src/utils/regionName/countries";
import { FieldWithError } from "src/molecules/FieldWithError";
import { useAutompleteAddressNl } from "src/hooks/useAutocompleteAddressNl";
import { Row } from "./AddressForm.styled";
import type { Address, Checkout } from "src/types/forms";

type FieldKey =
  | "streetName"
  | "streetNumber"
  | "additionalStreetInfo"
  | "postalCode"
  | "city"
  | "state"
  | "country"
  | "isStreetNumberNotRequired";

type Field = {
  type: "input" | "dropdown" | "checkbox";
  dataTestId: string;
  name: FieldKey;
  label: string;
  disabled: boolean;
  autoComplete?: string;
  options?: {
    key: string;
    text: string;
    value: string;
  }[];
};

type Fields = {
  [key in FieldKey]: Field;
};

type FormSubRow = (FieldKey | null)[];

type FormRow = (FieldKey | FormSubRow | null)[];

const getFields = (
  i18n: I18n,
  formik: FormikProps<Checkout> | FormikProps<Profile>,
  type: "checkout" | "profile",
  addressType: "shippingAddress" | "billingAddress",
  storeKey: StoreKey
): Fields => {
  const isCheckout = type === "checkout";
  const isCOMStoreKey = storeKey === "OT-COM";

  const isStreetNumberNotRequired =
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    formik.values[addressType].isStreetNumberNotRequired as boolean;
  return {
    streetName: {
      type: "input",
      dataTestId: `${type}-address-form-street-name`,
      name: "streetName",
      label: `${i18n._(t`Street`)} *`,
      disabled: false,
    },
    streetNumber: {
      type: "input",
      dataTestId: `${type}-address-form-street-number`,
      name: "streetNumber",
      label: `${i18n._(t`Number`)}${isStreetNumberNotRequired ? "" : " *"}`,
      disabled: false,
      autoComplete: "force-off",
    },
    additionalStreetInfo: {
      type: "input",
      dataTestId: `${type}-address-form-additional-street-info`,
      name: "additionalStreetInfo",
      label: `${i18n._(t`Addition`)}`,
      disabled: false,
      autoComplete: "force-off",
    },
    postalCode: {
      type: "input",
      dataTestId: `${type}-address-form-postal-code`,
      name: "postalCode",
      label: `${i18n._(t`Postcode`)} *`,
      disabled: false,
    },
    city: {
      type: "input",
      dataTestId: `${type}-address-form-city`,
      name: "city",
      label: `${i18n._(t`City`)} *`,
      disabled: false,
    },
    state: {
      type: "dropdown",
      dataTestId: `${type}-address-form-state`,
      name: "state",
      label: `${i18n._(t`State`)} *`,
      disabled: false,
      options: usStateOptions,
    },
    country: {
      type: "dropdown",
      dataTestId: `${type}-address-form-country`,
      name: "country",
      label: `${i18n._(t`Country`)} *`,
      disabled: !isCOMStoreKey,
      options:
        isCheckout && isCOMStoreKey
          ? checkoutCOMCountryOptions
          : countryOptions,
    },
    isStreetNumberNotRequired: {
      type: "checkbox",
      dataTestId: `${type}-address-form-is-street-number-not-required`,
      name: "isStreetNumberNotRequired",
      label: i18n._(t`My address does not contain a street number`),
      disabled: false,
    },
  };
};

const getFormRows = (
  country: string,
  showAutocompleteAddressNlForm: boolean
): FormRow[] => {
  if (country === "FR") {
    return [
      ["streetNumber", "streetName"],
      ["isStreetNumberNotRequired"],
      ["postalCode", "city"],
      ["country"],
    ];
  }

  if (country === "NL") {
    return showAutocompleteAddressNlForm
      ? [
          ["postalCode", "city"],
          ["streetName", ["streetNumber", "additionalStreetInfo"]],
          ["country"],
        ]
      : [["postalCode", "streetNumber"]];
  }

  return [
    ["streetName", ["streetNumber", "additionalStreetInfo"]],
    ["postalCode", "city"],
    ["country"],
  ];
};

interface FormSubRowProps {
  formSubRow: FormSubRow;
  i18n: I18n;
  formik: FormikProps<Checkout> | FormikProps<Profile>;
  type: "checkout" | "profile";
  addressType: "shippingAddress" | "billingAddress";
}

const FormSubRow: FC<FormSubRowProps> = ({
  formSubRow,
  i18n,
  formik,
  type,
  addressType,
}) => {
  const fields = getFields(i18n, formik, type, addressType, storeKey);

  return (
    <Row className="sub" columns={formSubRow.length}>
      {formSubRow.map((formSubRowItem: FieldKey | null, index: number) => {
        return formSubRowItem ? (
          <FieldWithError
            formik={formik}
            key={`${type}-${addressType}-${fields[formSubRowItem].type}-${index}`}
            type={fields[formSubRowItem].type}
            formType={type}
            data-testid={fields[formSubRowItem].dataTestId}
            label={fields[formSubRowItem].label}
            field={`${addressType}.${fields[formSubRowItem].name}`}
            disabled={fields[formSubRowItem].disabled}
            options={fields[formSubRowItem].options}
            autoComplete={fields[formSubRowItem].autoComplete}
          />
        ) : null;
      })}
    </Row>
  );
};

interface FormRowProps {
  formRow: FormRow;
  formRowIndex: number;
  i18n: I18n;
  formik: FormikProps<Checkout> | FormikProps<Profile>;
  type: "checkout" | "profile";
  addressType: "shippingAddress" | "billingAddress";
}

function FormRow({
  formRow,
  i18n,
  formik,
  type,
  addressType,
}: FormRowProps): JSX.Element {
  const fields = getFields(i18n, formik, type, addressType, storeKey);

  return (
    <>
      {formRow.map(
        (formRowItem: FieldKey | FormSubRow | null, index: number) => {
          const isFieldExists = !!formRowItem;
          const isField = !Array.isArray(formRowItem);

          return (
            isFieldExists &&
            (isField ? (
              <FieldWithError
                key={`${type}-${addressType}-${fields[formRowItem].type}-${index}`}
                formik={formik}
                type={fields[formRowItem].type}
                formType={type}
                data-testid={fields[formRowItem].dataTestId}
                label={fields[formRowItem].label}
                field={`${addressType}.${fields[formRowItem].name}`}
                disabled={fields[formRowItem].disabled}
                options={fields[formRowItem].options}
                autoComplete={fields[formRowItem].autoComplete}
              />
            ) : (
              <FormSubRow
                key={`${type}-${addressType}-subrow-${index}`}
                formSubRow={formRowItem}
                i18n={i18n}
                formik={formik}
                type={type}
                addressType={addressType}
              />
            ))
          );
        }
      )}
    </>
  );
}

interface AddressFormProps {
  formik: FormikProps<Checkout> | FormikProps<Profile>;
  formikValuesAddress: Address | undefined;
  formikErrorsAddress: FormikErrors<Address> | undefined;
  formikTouchedAddress: FormikTouched<Address> | undefined;
  type: "checkout" | "profile";
  addressType: "shippingAddress" | "billingAddress";
}

const AddressForm = ({
  formik,
  formikValuesAddress,
  formikErrorsAddress,
  formikTouchedAddress,
  type,
  addressType,
}: AddressFormProps): JSX.Element | null => {
  const showAutocompleteAddressNlForm = useAutompleteAddressNl(
    formik,
    formikValuesAddress,
    formikErrorsAddress,
    formikTouchedAddress,
    addressType
  );

  const { i18n } = useLingui();

  if (!formikValuesAddress) {
    return null;
  }

  return (
    <>
      {getFormRows(
        formikValuesAddress.country,
        showAutocompleteAddressNlForm
      ).map((formRow: FormRow, index: number) => {
        const numberOfRowItems = formRow.length;

        return (
          <Row
            key={`${type}-${addressType}-row-${index}`}
            columns={numberOfRowItems}
          >
            <FormRow
              formRow={formRow}
              formRowIndex={index}
              i18n={i18n}
              formik={formik}
              type={type}
              addressType={addressType}
            />
          </Row>
        );
      })}
    </>
  );
};

export default AddressForm;
